import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { engagementRequirementKeys } from './engagement-requirements';
import { fetchSpreadsheetFile, type ExportedFile } from './requirement-spreadsheets';
import { uploadWithProgress, type UploadProgressHandlers } from './upload';

/**
 * Match a spreadsheet to the library (T-0264; E14-design §2). Routes in
 * `apps/api/src/routes/requirement-matches.ts`.
 *
 *   - The upload is the import's: the file is the raw body, its name in
 *     `X-File-Name`. It answers 202 with the run `matching`; the screen
 *     polls the run until the judge has finished.
 *   - Decisions save one row at a time, or many at once for the bulk
 *     actions. Apply needs an `Idempotency-Key`, reused on a retry.
 */

export const MATCH_DECISIONS = ['add_library', 'add_as_written', 'skip'] as const;
export type MatchDecision = (typeof MATCH_DECISIONS)[number];
export type MatchVerdict = 'same' | 'broader' | 'none';

const CandidateSchema = z.object({
  library_item_id: z.string(),
  number: z.string(),
  module_id: z.string(),
  module_name: z.string(),
  text: z.string(),
  /**
   * Code's similarity score: the cosine of a match by meaning, the word
   * overlap of a wording match; null when the row stored none (or an older
   * server).
   */
  score: z.number().nullable().optional(),
  /** `exact`, `wording` or `meaning`. */
  match_kind: z.string().nullable().optional(),
});
export type MatchCandidate = z.infer<typeof CandidateSchema>;

const NamedSchema = z.object({ id: z.string(), name: z.string() });

export const MatchRowSchema = z.object({
  id: z.string(),
  sheet: z.string(),
  row_number: z.number().int(),
  client_ref: z.string().nullable(),
  client_area: z.string().nullable(),
  text: z.string(),
  criticality: z.string(),
  verdict: z.enum(['same', 'broader', 'none']).nullable(),
  reason: z.string().nullable(),
  chosen: CandidateSchema.nullable(),
  candidates: z.array(CandidateSchema),
  already_present: z.boolean(),
  decision: z.enum(MATCH_DECISIONS).nullable(),
  decision_library_item_id: z.string().nullable(),
  decision_module: NamedSchema.nullable(),
  decided_by: z.string().nullable(),
  decided_by_name: z.string().nullable(),
  decided_at: z.string().nullable(),
  /** The module the client area names; null when none matches it by name (the row goes in Not mapped). */
  suggested_module: NamedSchema.nullable(),
  /** Why that module: `area_name`; an older server may send the removed vote's bases. */
  suggested_module_basis: z.string().nullable().optional(),
  requirement: z.object({ id: z.string(), number: z.string(), status: z.string() }).nullable(),
  ready: z.boolean(),
});
export type MatchRow = z.infer<typeof MatchRowSchema>;

const CountsSchema = z.object({
  rows: z.number().int(),
  matched: z.number().int(),
  same: z.number().int(),
  broader: z.number().int(),
  none: z.number().int(),
  already_present: z.number().int(),
  ready: z.number().int(),
  added: z.number().int(),
  skipped: z.number().int(),
  undecided: z.number().int(),
});
export type MatchRunCounts = z.infer<typeof CountsSchema>;

export const MatchRunSummarySchema = z.object({
  id: z.string(),
  file_name: z.string(),
  status: z.enum(['matching', 'ready', 'applied', 'failed']),
  reason: z.string().nullable(),
  created_at: z.string(),
  created_by: z.string(),
  created_by_name: z.string().nullable(),
  last_applied_at: z.string().nullable(),
  counts: CountsSchema,
});
export type MatchRunSummary = z.infer<typeof MatchRunSummarySchema>;

export const MatchRunSchema = MatchRunSummarySchema.extend({
  groups: z.object({
    same: z.array(MatchRowSchema),
    broader: z.array(MatchRowSchema),
    none: z.array(MatchRowSchema),
    unmatched: z.array(MatchRowSchema),
  }),
});
export type MatchRun = z.infer<typeof MatchRunSchema>;

const MatchRunListSchema = z.object({ items: z.array(MatchRunSummarySchema) });

export const ApplyResultSchema = z.object({
  load_id: z.string(),
  added: z.number().int(),
  added_as_written: z.number().int(),
  restored: z.number().int(),
  already_present: z.number().int(),
  not_applied: z.number().int(),
  rows: z.array(
    z.object({
      row_id: z.string(),
      outcome: z.string(),
      requirement_id: z.string().nullable(),
      number: z.string().nullable(),
    }),
  ),
  run: MatchRunSummarySchema,
});
export type ApplyResult = z.infer<typeof ApplyResultSchema>;

export interface DecisionInput {
  decision: MatchDecision | null;
  library_item_id?: string;
  /** For add_as_written: a library module; none puts the row in Not mapped. */
  module_id?: string;
}

/** What "Restore the spreadsheet's wording and areas" would change, or changed. */
export const RestorePreviewSchema = z.object({
  run_id: z.string(),
  file_name: z.string(),
  requirements: z.number().int(),
  to_change: z.number().int(),
  text: z.number().int(),
  text_kept_edited: z.number().int(),
  client_area: z.number().int(),
  not_mapped: z.number().int(),
  not_mapped_by_area: z.array(
    z.object({ area: z.string(), module_name: z.string(), count: z.number().int() }),
  ),
  removed_skipped: z.number().int(),
});
export type RestorePreview = z.infer<typeof RestorePreviewSchema>;

export const RestoreResultSchema = RestorePreviewSchema.extend({
  changed: z.array(z.object({ id: z.string(), number: z.string() })),
});
export type RestoreResult = z.infer<typeof RestoreResultSchema>;

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function base(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/requirements/matches`;
}

function runPath(engagementId: string, runId: string): string {
  return `${base(engagementId)}/${encodeURIComponent(runId)}`;
}

export function uploadMatchRun(
  engagementId: string,
  file: File,
  signal?: AbortSignal,
  progress?: UploadProgressHandlers,
): Promise<MatchRun> {
  return uploadWithProgress(base(engagementId), file, MatchRunSchema, {
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-File-Name': encodeURIComponent(file.name),
    },
    signal,
    ...progress,
  });
}

export function getMatchRun(engagementId: string, runId: string): Promise<MatchRun> {
  return apiClient(runPath(engagementId, runId), MatchRunSchema);
}

/** The run's status and counts, no rows: the poll while the judge works. */
export function getMatchRunSummary(engagementId: string, runId: string): Promise<MatchRunSummary> {
  return apiClient(runPath(engagementId, runId), MatchRunSummarySchema, {
    query: { view: 'summary' },
  });
}

export function listMatchRuns(engagementId: string) {
  return apiClient(base(engagementId), MatchRunListSchema);
}

export function decideMatchRow(
  engagementId: string,
  runId: string,
  rowId: string,
  input: DecisionInput,
): Promise<MatchRow> {
  return apiClient(
    `${runPath(engagementId, runId)}/rows/${encodeURIComponent(rowId)}`,
    MatchRowSchema,
    { method: 'PATCH', body: input },
  );
}

export function decideMatchRows(
  engagementId: string,
  runId: string,
  decisions: ReadonlyArray<DecisionInput & { row_id: string }>,
): Promise<MatchRun> {
  return apiClient(`${runPath(engagementId, runId)}/rows:decide`, MatchRunSchema, {
    method: 'POST',
    body: { decisions },
  });
}

export function applyMatchRun(
  engagementId: string,
  runId: string,
  idempotencyKey: string,
): Promise<ApplyResult> {
  return apiClient(`${runPath(engagementId, runId)}:apply`, ApplyResultSchema, {
    method: 'POST',
    idempotencyKey,
  });
}

export function runMatchAgain(engagementId: string, runId: string): Promise<MatchRunSummary> {
  return apiClient(`${runPath(engagementId, runId)}:run`, MatchRunSummarySchema, {
    method: 'POST',
  });
}

export function previewMatchRestore(engagementId: string, runId: string): Promise<RestorePreview> {
  return apiClient(`${runPath(engagementId, runId)}/restore`, RestorePreviewSchema);
}

export function restoreMatchRun(
  engagementId: string,
  runId: string,
  idempotencyKey: string,
): Promise<RestoreResult> {
  return apiClient(`${runPath(engagementId, runId)}:restore`, RestoreResultSchema, {
    method: 'POST',
    idempotencyKey,
  });
}

/**
 * The Broader and No match rows as .xlsx, or every row of the run (`all`),
 * each with its candidates' meaning scores.
 */
export function downloadMatchReview(
  engagementId: string,
  runId: string,
  rows: 'review' | 'all' = 'review',
): Promise<ExportedFile> {
  return rows === 'all'
    ? fetchSpreadsheetFile(`${runPath(engagementId, runId)}/review.xlsx?rows=all`, 'all-rows.xlsx')
    : fetchSpreadsheetFile(`${runPath(engagementId, runId)}/review.xlsx`, 'review-list.xlsx');
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const requirementMatchKeys = {
  all: (engagementId: string) =>
    [...engagementRequirementKeys.all(engagementId), 'matches'] as const,
  list: (engagementId: string) => [...requirementMatchKeys.all(engagementId), 'list'] as const,
  run: (engagementId: string, runId: string) =>
    [...requirementMatchKeys.all(engagementId), 'run', runId] as const,
  restore: (engagementId: string, runId: string) =>
    [...requirementMatchKeys.all(engagementId), 'restore', runId] as const,
};

/** How often a run being matched is read again. */
export const MATCH_POLL_MS = 2000;

export function useMatchRunsQuery(engagementId: string, enabled: boolean) {
  return useQuery({
    queryKey: requirementMatchKeys.list(engagementId),
    queryFn: () => listMatchRuns(engagementId),
    enabled,
    staleTime: 0,
  });
}

/**
 * One run. While the judge works on it, only its status and counts are read
 * again, every two seconds; the rows are read once more when it is done.
 */
export function useMatchRunQuery(engagementId: string, runId: string | null) {
  const qc = useQueryClient();
  const key = requirementMatchKeys.run(engagementId, runId ?? '');
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const cached = qc.getQueryData<MatchRun>(key);
      if (cached?.status === 'matching') {
        const summary = await getMatchRunSummary(engagementId, runId!);
        if (summary.status === 'matching') return { ...cached, ...summary };
      }
      return getMatchRun(engagementId, runId!);
    },
    enabled: runId !== null,
    refetchInterval: (query) => (query.state.data?.status === 'matching' ? MATCH_POLL_MS : false),
  });
}

/** One row's decision; the run is patched in place with the row the server returns. */
export function useDecideMatchRow(engagementId: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { rowId: string; input: DecisionInput }) =>
      decideMatchRow(engagementId, runId, args.rowId, args.input),
    onSuccess: (row) => {
      qc.setQueryData<MatchRun>(requirementMatchKeys.run(engagementId, runId), (run) =>
        run ? replaceRow(run, row) : run,
      );
    },
  });
}

export function useDecideMatchRows(engagementId: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (decisions: ReadonlyArray<DecisionInput & { row_id: string }>) =>
      decideMatchRows(engagementId, runId, decisions),
    onSuccess: (run) => qc.setQueryData(requirementMatchKeys.run(engagementId, runId), run),
  });
}

/** Apply, then refetch the run and the engagement's requirements. */
export function useApplyMatchRun(engagementId: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idempotencyKey: string) => applyMatchRun(engagementId, runId, idempotencyKey),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: engagementRequirementKeys.all(engagementId) }),
  });
}

/** The restore preview of a run some of whose rows were added. */
export function useMatchRestorePreview(engagementId: string, runId: string, enabled: boolean) {
  return useQuery({
    queryKey: requirementMatchKeys.restore(engagementId, runId),
    queryFn: () => previewMatchRestore(engagementId, runId),
    enabled,
    staleTime: 0,
  });
}

/** Restore, then read the requirements (and the preview) again. */
export function useRestoreMatchRun(engagementId: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idempotencyKey: string) => restoreMatchRun(engagementId, runId, idempotencyKey),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: engagementRequirementKeys.all(engagementId) }),
  });
}

export function useRunMatchAgain(engagementId: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => runMatchAgain(engagementId, runId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: requirementMatchKeys.run(engagementId, runId) }),
  });
}

/** The run with one row replaced and the counts read again from the rows. */
export function replaceRow(run: MatchRun, row: MatchRow): MatchRun {
  const swap = (rows: MatchRow[]) => rows.map((r) => (r.id === row.id ? row : r));
  const groups = {
    same: swap(run.groups.same),
    broader: swap(run.groups.broader),
    none: swap(run.groups.none),
    unmatched: swap(run.groups.unmatched),
  };
  const all = [...groups.same, ...groups.broader, ...groups.none, ...groups.unmatched];
  return {
    ...run,
    groups,
    counts: {
      ...run.counts,
      ready: all.filter((r) => r.ready).length,
      skipped: all.filter((r) => r.decision === 'skip').length,
      undecided: all.filter((r) => r.requirement === null && r.decision === null && !r.ready)
        .length,
    },
  };
}
